import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  Divider,
  Avatar,
} from '@mui/material';
import { Visibility, VisibilityOff, PersonAddAlt, Email, Lock, AccountCircle } from '@mui/icons-material';
import './register.css';
import { registerUser } from './Service';

type Gender = 'MALE' | 'FEMALE' | 'OTHER';

interface RegisterFormData {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  gender: Gender;
  password: string;
  confirmPassword: string;
}

const Register: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<RegisterFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      username: '',
      email: '',
      gender: '' as unknown as Gender,
      password: '',
      confirmPassword: '',
    },
    mode: 'onChange',
  });

  const password = watch('password');

  const onSubmit = async (data: RegisterFormData) => {
    try {
      setIsLoading(true);
      setError('');
      
      const { confirmPassword, ...registerData } = data;
      
      // Gọi API đăng ký
      const response = await registerUser({
        firstName: data.firstName,
        lastName: data.lastName,
        username: data.username,
        email: data.email,
        gender: data.gender,
        password: data.password,
      });
      
      setSuccess('Đăng ký thành công! Bạn sẽ được chuyển đến trang đăng nhập sau 3 giây...');
      
      // Chuyển hướng sau 3 giây
      setTimeout(() => {
      navigate('/login');
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Đã có lỗi xảy ra khi đăng ký');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <Container className="mui-container" maxWidth={false} disableGutters>
      <Paper className="mui-paper" elevation={6}>
        <Box sx={{ display: 'flex', flexDirection: {xs: 'column', md: 'row'}, height: '100%' }}>
          {/* Left side - Brand image */}
          <Box 
            sx={{ 
              flex: {xs: '1', md: '4'}, 
              background: '#f0f0f0',
              p: 4,
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              color: '#333',
              position: 'relative',
              overflow: 'hidden',
              display: {xs: 'none', md: 'flex'}
            }}
            className="register-banner"
          >
            <Box 
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.25,
                backgroundImage: 'url("background.e79f8e9d.jpg")',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                zIndex: 0
              }}
            />
            <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
              <img src="/Vis-tracker-logo.png" alt="Vissoft Tracker Logo" style={{ height: '100px', marginBottom: '1.5rem' }} />
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Vissoft Tracker
              </Typography>
              <Typography variant="h6" sx={{ mb: 4 }}>
                Theo dõi và quản lý hoạt động chạy bộ của bạn
              </Typography>
            </Box>
          </Box>

          {/* Right side - Registration form */}
          <Box sx={{ flex: {xs: '1', md: '6'}, p: {xs: 3, sm: 4}, display: 'flex', flexDirection: 'column' }}>
            <Box textAlign="center" sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: '#20757A', width: 56, height: 56 }}>
                  <PersonAddAlt fontSize="large" />
                </Avatar>
              </Box>
              <Typography variant="h5" component="h1" fontWeight="bold" color="#2B2F30">
                Đăng ký tài khoản mới
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Tham gia cùng chúng tôi để theo dõi và cải thiện hiệu suất chạy bộ
              </Typography>
            </Box>
            
            {/* Alert messages */}
            {error && (
              <Box className="error-message-animate" sx={{
                backgroundColor: '#fff0f0',
                border: '1px solid #ffcccc',
                borderRadius: 1,
                color: '#cc0000',
                p: 1.5,
                width: '100%',
                mb: 2,
              }}>
                {error}
              </Box>
            )}

            {success && (
              <Box className="success-message-animate" sx={{
                backgroundColor: '#f0fff0',
                border: '1px solid #ccffcc',
                borderRadius: 1,
                color: '#008800',
                p: 1.5,
                width: '100%',
                mb: 2,
              }}>
                {success}
              </Box>
            )}

            <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)} sx={{ width: '100%' }}>
              <Stack spacing={2.5}>
                <Typography variant="subtitle2" color="primary" fontWeight="medium" sx={{ mt: 1 }}>
                  Thông tin cá nhân
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Box sx={{ width: '100%' }}>
                    <Controller
                      name="firstName"
                      control={control}
                      rules={{
                        required: 'Vui lòng nhập họ',
                        minLength: {
                          value: 2,
                          message: 'Họ phải có ít nhất 2 ký tự',
                        },
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder="Họ"
                          variant="outlined"
                          InputLabelProps={{ shrink: false }}
                          error={!!errors.firstName}
                          helperText={errors.firstName?.message}
                          sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#20757A' } }}
                          className="form-field-animate"
                        />
                      )}
                    />
                  </Box>
                  <Box sx={{ width: '100%' }}>
                    <Controller
                      name="lastName"
                      control={control}
                      rules={{
                        required: 'Vui lòng nhập tên',
                        minLength: {
                          value: 2,
                          message: 'Tên phải có ít nhất 2 ký tự',
                        },
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder="Tên"
                          variant="outlined"
                          InputLabelProps={{ shrink: false }}
                          error={!!errors.lastName}
                          helperText={errors.lastName?.message}
                          sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#20757A' } }}
                          className="form-field-animate"
                        />
                      )}
                    />
                  </Box>
                </Stack>

                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="primary" fontWeight="medium">
                  Thông tin tài khoản
                </Typography>

                <Controller
                  name="username"
                  control={control}
                  rules={{
                    required: 'Vui lòng nhập tên đăng nhập',
                    minLength: {
                      value: 4,
                      message: 'Tên đăng nhập phải có ít nhất 4 ký tự',
                    },
                    pattern: {
                      value: /^[a-zA-Z0-9_]+$/,
                      message: 'Tên đăng nhập chỉ được chứa chữ cái, số và dấu gạch dưới',
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      placeholder="Tên đăng nhập"
                      variant="outlined"
                      InputLabelProps={{ shrink: false }}
                      error={!!errors.username}
                      helperText={errors.username?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AccountCircle color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#20757A' } }}
                      className="form-field-animate"
                    />
                  )}
                />
          
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: 'Vui lòng nhập email',
                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                      message: 'Email không hợp lệ',
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      placeholder="Email"
                      variant="outlined"
                      InputLabelProps={{ shrink: false }}
                      type="email"
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#20757A' } }}
                      className="form-field-animate"
                    />
                  )}
                />
          
                <Controller
                  name="gender"
                  control={control}
                  rules={{ required: 'Vui lòng chọn giới tính' }}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.gender} className="form-field-animate">
                      <Select
                        {...field}
                        displayEmpty
                        variant="outlined"
                        renderValue={(value) => {
                          if (!value) {
                            return <em style={{ color: '#757575' }}>Giới tính</em>;
                          }
                          return value === 'MALE' ? 'Nam' : value === 'FEMALE' ? 'Nữ' : 'Khác';
                        }}
                        sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#20757A' } }}
                      >
                        <MenuItem value=""></MenuItem>
                        <MenuItem value="MALE">Nam</MenuItem>
                        <MenuItem value="FEMALE">Nữ</MenuItem>
                        <MenuItem value="OTHER">Khác</MenuItem>
                      </Select>
                      {errors.gender && <FormHelperText>{errors.gender.message}</FormHelperText>}
                    </FormControl>
                  )}
                />

                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="primary" fontWeight="medium">
                  Mật khẩu
                </Typography>
          
                <Controller
                  name="password"
                  control={control}
                  rules={{
                    required: 'Vui lòng nhập mật khẩu',
                    minLength: {
                      value: 8,
                      message: 'Mật khẩu phải có ít nhất 8 ký tự',
                    },
                    pattern: {
                      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/,
                      message: 'Mật khẩu phải chứa ít nhất 1 chữ hoa, 1 chữ thường, 1 số và 1 ký tự đặc biệt',
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      placeholder="Mật khẩu"
                      variant="outlined"
                      InputLabelProps={{ shrink: false }}
                      type="password"
                      error={!!errors.password}
                      helperText={errors.password?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock color="action" />
                          </InputAdornment>
                        )
                      }}
                      sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#20757A' } }}
                      className="form-field-animate"
                    />
                  )}
                />
          
                <Controller
                  name="confirmPassword"
                  control={control}
                  rules={{
                    required: 'Vui lòng xác nhận mật khẩu',
                    validate: (value) => value === password || 'Mật khẩu xác nhận không khớp',
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      placeholder="Xác nhận mật khẩu"
                      variant="outlined"
                      InputLabelProps={{ shrink: false }}
                      type="password"
                      error={!!errors.confirmPassword}
                      helperText={errors.confirmPassword?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock color="action" />
                          </InputAdornment>
                        )
                      }}
                      sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#20757A' } }}
                      className="form-field-animate"
                    />
                  )}
                />
              </Stack>
          
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                className="submit-button-animate"
                sx={{
                  mt: 4,
                  mb: 2,
                  py: 1.5,
                  backgroundColor: '#FF8C00',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 10px rgba(255, 140, 0, 0.3)',
                  '&:hover': {
                    backgroundColor: '#FF7000',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 15px rgba(255, 140, 0, 0.4)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                    boxShadow: '0 2px 5px rgba(255, 140, 0, 0.3)',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(255, 140, 0, 0.6)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                {isLoading ? 'Đang xử lý...' : 'Đăng ký ngay'}
              </Button>

              <Box sx={{ textAlign: 'center', mt: 3, p: 2, bgcolor: 'rgba(0,0,0,0.03)', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Bạn đã có tài khoản?
                </Typography>
                <Link to="/login" className="mui-link">
                  <Typography variant="subtitle2" fontWeight="bold">
                    Đăng nhập
                  </Typography>
                </Link>
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default Register; 